import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// note: MUI import
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

// note: import state.
import {
  authAction,
  selectRegisterInput,
  selectRegisterStep,
  selectAuth,
  selectVerifyRegisterCodeInput,
} from "state/reducer/auth";
import {
  verifyAction,
  selectVerify,
  selectReRequestCode,
} from "state/reducer/verify";
import { userAction } from "state/reducer/user";

// note: component state
import { MCButtonBV1, MCTextButtonBV2 } from "components/Reusable/Button";
import {
  MCTextFieldBV2,
  MCEmailTextFieldBV5,
  MCPasswordTextFieldBV6,
} from "components/Reusable/TextField";
import { MCCirculerNonValueBV2 } from "components/Reusable/Progress";
import PasswordStrengthIndicator from "components/Reusable/PasswordStrengthIndicator";

// note: utils import
import {
  undraw_completing_re_i7ap,
  HomeIcon,
  VerifiedIcon,
  SendIcon,
} from "utils/asset";
import { sliceEmail } from "utils/email";
import { cookies } from "utils/cookies";
import { validateEmail } from "utils/validate";

// ==============================|| FORM - REGISTER ||============================== //
export function RegisterForm({ steps }) {
  // note: declare hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // note: declare states
  const registerInput = useSelector(selectRegisterInput);
  const registerStep = useSelector(selectRegisterStep);
  const verifyCodeRegisterInput = useSelector(selectVerifyRegisterCodeInput);
  const register = useSelector(selectAuth)?.register;
  const checkVerifyCode = useSelector(selectVerify)?.checkVerifyCode;
  const reSendVerifyCode = useSelector(selectVerify)?.sendVerifyCode;
  const reRequestCode = useSelector(selectReRequestCode);
  const getCompany = useSelector(selectAuth)?.getCompanyByUserHasRegistered;
  const fillBiodataInput = useSelector(selectAuth)?.fill_biodata_input;
  const fillBiodata = useSelector(selectAuth)?.fillBiodata;

  // note: handle show password
  const [show, setShow] = React.useState({
    password: false,
    confirmPassword: false,
  });

  function handleMouseDownPassword(event) {
    event.preventDefault();
  }

  // note: handle go to home
  function handleGoToHome() {
    Promise.allSettled([
      dispatch(authAction.setClearRegisterInput()),
      dispatch(authAction.setClearFillBiodata()),
      dispatch(
        authAction.setRegisterStep({ ...registerStep, isComplated: true })
      ),
      navigate("/", { replace: true }),
    ]);
  }

  // note: handle register
  function handleRegisterInput(event) {
    const { name, value } = event.target;

    dispatch(
      authAction.setRegisterInput({
        register_input: { ...registerInput, [name]: value },
      })
    );
  }

  function registerCbFulfilled() {
    dispatch(
      authAction.setRegisterStep({
        step: registerStep.step + 1,
        isComplated: false,
      })
    );
  }

  async function handleCreateAccount() {
    dispatch(
      authAction.registerThunk({
        pathname: "/register",
        body: registerInput,
        cbFulfilled: registerCbFulfilled,
      })
    );
  }

  // note: handle verify account code
  function handleVerifyRegisterCodeInput(event) {
    const { name, value } = event.target;
    dispatch(
      authAction.setVerifyRegisterCodeInput({
        ...verifyCodeRegisterInput,
        [name]: value,
      })
    );
  }

  // note: verify register fulfilled callback
  async function verifyRegisterCbFulfilled(response) {
    Promise.allSettled([
      cookies.set(
        "token",
        JSON.stringify({
          token: response.token,
        }),
        { expires: new Date(Date.now() + response?.expiryToken) }
      ),
      cookies.set(
        "refreshToken",
        JSON.stringify({
          refreshToken: response.refreshToken,
        }),
        { expires: new Date(Date.now() + response?.expiryRefreshToken) }
      ),
      dispatch(
        authAction.setRegisterStep({
          step: registerStep.step + 1,
          isComplated: false,
        })
      ),
      dispatch(authAction.setClearVerifyRegisterCodeInput()),

      // note: get profile
      dispatch(
        userAction.getProfileThunk({
          pathname: "/user/profile",
          accessToken: response?.token || response?.refreshToken,
        })
      ),
    ]);

    if (
      cookies.get("token")?.token ||
      cookies.get("refreshToken")?.refreshToken
    ) {
      dispatch(
        authAction.getCompanyByUserHasRegisteredThunk({
          pathname: "/company",
          queryParams: `?domain=${sliceEmail(registerInput?.email)}`,
          accessToken:
            cookies.get("token")?.token ||
            cookies.get("refreshToken")?.refreshToken,
        })
      );
    }
  }

  // note: verify register rejected callback
  async function verifyRegisterCbRejected(response) {
    if (response.message === "Failed verify") {
      dispatch(
        verifyAction.setReRequestCode({ ...reRequestCode, isVisibile: true })
      );
    }
  }

  // note: handle veridy register code
  async function handleVerifyRegisterCode() {
    dispatch(
      verifyAction.checkVerifyCodeThunk({
        pathname: "/verify",
        queryParams: `?type=register&email=${registerInput?.email}`,
        body: { otp: verifyCodeRegisterInput?.otp },
        cbFulfilled: verifyRegisterCbFulfilled,
        cbRejected: verifyRegisterCbRejected,
      })
    );
  }

  // note: handle re - request verify register code
  function sendVerifyCodeCbFulfilled() {
    dispatch(verifyAction.setClearReRequestCode());
  }

  // note: handle re - request verify register code
  async function handleReRequestVerifyRegisterCode() {
    dispatch(
      verifyAction.sendVerifyCodeThunk({
        pathname: "/verify/send-code",
        queryParams: "?type=register",
        body: { email: registerInput?.email },
        cbFulfilled: sendVerifyCodeCbFulfilled,
        accessToken:
          cookies.get("token")?.token ||
          cookies.get("refreshToken")?.refreshToken,
      })
    );
  }

  // note: handle choose fill biodata
  async function handleFillBiodataInput(event) {
    const { name, value } = event.target;
    dispatch(
      authAction.setFillBodataInput({ ...fillBiodataInput, [name]: value })
    );
  }

  // note: handle biodata fulfilled callback
  async function handleBiodataCbFulfilled() {
    Promise.allSettled([
      dispatch(authAction.setClearStep()),
      dispatch(authAction.setClearRegisterInput()),
      dispatch(authAction.setClearFillBiodata()),
      dispatch(authAction.setClearFillBiodataInput()),
      dispatch(
        authAction.setRegisterStep({
          step: registerStep.step + 1,
          isComplated: false,
        })
      ),
    ]);
  }

  // note: handle fill biodata
  async function handleFillBiodata() {
    dispatch(
      authAction.fillBiodataThunk({
        accessToken:
          cookies?.get("token")?.token ||
          cookies?.get("refreshToken")?.refreshToken,
        pathname: "/user/profile/update",
        body: {
          name: fillBiodataInput?.name,
          company: getCompany?.data?.data[0]?.code,
        },
        cbFulfilled: handleBiodataCbFulfilled,
      })
    );
  }

  // note: the script going to execute if fillbiodata's state is is rejected.
  React.useEffect(() => {
    if (fillBiodata?.isRejected) {
      dispatch(
        authAction.getCompanyByUserHasRegisteredThunk({
          pathname: "/company",
          queryParams: `?domain=${sliceEmail(registerInput?.email)}`,
          accessToken:
            cookies.get("token")?.token ||
            cookies.get("refreshToken")?.refreshToken,
        })
      );
    }
  }, [fillBiodata?.isRejected]);

  return (
    <Box component={"section"}>
      <Stack spacing={3}>
        <MCTextFieldBV2
          type={"text"}
          label={"Fullname"}
          name={"name"}
          value={fillBiodataInput?.name}
          onChange={handleFillBiodataInput}
          shrink={true}
        />

        <MCEmailTextFieldBV5
          label={"Email"}
          name={"email"}
          // value={bodyLoginInput.email}
          // onChange={handleLoginInput}
          variant={"standard"}
        />

        <MCPasswordTextFieldBV6
          label={"Password"}
          name={"password"}
          // value={bodyLoginInput?.password}
          // onChange={handleLoginInput}
          // onMouseDown={handleMouseDownPassword}
          // type={show.password ? "text" : "password"}
          // onClick={() => handleShow({ ...show, password: !show.password })}
          // showPassword={show.password}
          variant={"standard"}
        />

        <MCButtonBV1
          variant={"contained"}
          title={"Sign Up"}
          type={"button"}
          endIcon={
            reSendVerifyCode?.isPending || fillBiodata.isPending ? (
              <MCCirculerNonValueBV2 size={"1.3rem"} />
            ) : (
              <SendIcon />
            )
          }
          // disabled={
          //   reSendVerifyCode?.isPending ||
          //   Object.values(fillBiodataInput).includes("") ||
          //   fillBiodata.isPending
          // }
          onClick={handleFillBiodata}
        />

        <Box component={"section"}>
          <Stack spacing={1}>
            <MCTextButtonBV2
              text={"Forget Password ?"}
              onClick={() => navigate("/password/forgot", { replace: true })}
              sx={{
                fontSize: "14px",
                textAlign: "start",
              }}
            />
            <MCTextButtonBV2
              text={"Login"}
              onClick={() => navigate("/login", { replace: true })}
              sx={{
                fontSize: "12px",
                textAlign: "start",
              }}
              description={"Have an account ?"}
            />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
